import { AppConfig } from '@ltvco/refresh-lib/ctx';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Input,
  Stack,
  Text,
} from '@ltvco/refresh-lib/theme';
import { useAccount } from '@ltvco/refresh-lib/v1';
import { useForm, yupResolver } from '@ltvco/refresh-lib/vendors';
import { Close } from '@mui/icons-material';
import { useContext, useState } from 'react';

import {
  ESTIMATED_REQUESTS_OPTIONS,
  INDUSTRY_OPTIONS,
  type ModalVariant,
  VARIANT_CONFIG,
} from 'routes/Dashboard/ApiPage/constants';
import { schema, type FormValues } from 'routes/Dashboard/ApiPage/schema';
import { SelectField } from 'routes/Dashboard/ApiPage/SelectField';
import { SuccessView } from 'routes/Dashboard/ApiPage/SuccessView';
import { UseCasesField } from 'routes/Dashboard/ApiPage/UseCasesField';
import { useEnterpriseLead } from 'routes/Dashboard/ApiPage/useEnterpriseLead';
import { useHubSpotLead } from 'routes/Dashboard/ApiPage/useHubSpotLead';

const placeholderSx = {
  '& .MuiInputBase-input::placeholder': {
    textTransform: 'uppercase',
    fontSize: '0.8rem',
    opacity: 1,
    color: 'text.disabled',
  },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiOutlinedInput-root': { borderRadius: '10px', height: '50px' },
  '& .MuiInputBase-input': { padding: '0 14px' },
};

const textareaSx = {
  '& .MuiInputBase-input': { color: 'text.primary' },
  '& .MuiInputBase-input::placeholder': {
    fontSize: '0.8rem',
    opacity: 1,
    color: 'text.secondary',
    fontStyle: 'italic',
  },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    height: 'auto',
    alignItems: 'flex-start',
  },
};

const sectionBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: '10px',
  p: 2,
};

interface SalesLeadModalProps {
  open: boolean;
  onClose: () => void;
  variant?: ModalVariant;
}

export function SalesLeadModal({
  open,
  onClose,
  variant = 'api',
}: SalesLeadModalProps) {
  const { trackEventGA4 } = useContext(AppConfig);
  const {
    data: account,
    isLoading: isAccountLoading,
    isError: isAccountError,
  } = useAccount(true);
  const [submitted, setSubmitted] = useState(false);

  const config = VARIANT_CONFIG[variant];
  const lead = useEnterpriseLead();
  const hubspot = useHubSpotLead();

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      industry: '',
      industryOther: '',
      useCases: [],
      useCaseOther: '',
      useCaseDescription: '',
      estimatedRequests: '',
    },
  });

  const industry = watch('industry');

  const handleClose = () => {
    reset();
    lead.reset();
    hubspot.reset();
    setSubmitted(false);
    onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (isAccountLoading || isAccountError || !account) {
      return;
    }
    const industryValue =
      values.industry === 'Other'
        ? values.industryOther
          ? `Other - ${values.industryOther}`
          : 'Other'
        : values.industry;

    const selectedCases = (values.useCases ?? [])
      .filter((c) => c !== 'Other')
      .concat(
        values.useCases?.includes('Other') && values.useCaseOther
          ? [`Other - ${values.useCaseOther}`]
          : []
      );
    const useCaseValue = (
      config.useCasePrefix
        ? [config.useCasePrefix, ...selectedCases]
        : selectedCases
    ).join(', ');

    await lead.mutateAsync({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      company: values.company,
      industry: industryValue,
      estimatedSearches: values.estimatedRequests,
      userCode: account.account?.user_info?.user_code,
    });

    await hubspot.mutateAsync({
      email: values.email,
      firstname: values.firstName,
      lastname: values.lastName,
      phone: values.phone,
      company_website: values.company,
      industry: industryValue,
      use_case: useCaseValue,
      use_case_description: values.useCaseDescription,
      estimated_searches: values.estimatedRequests,
      member_id: account?.account?.user_info?.user_code,
    });

    trackEventGA4({
      event_name: 'contact_form_click',
      type: config.ga4Type,
      action: 'submit',
    });
    setSubmitted(true);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box
        sx={{ position: 'relative', textAlign: 'center', pt: 4, px: 4, pb: 2 }}
      >
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <Close />
        </IconButton>
        <Text variant="h4" fontWeight="bold" mb={1}>
          {submitted ? 'Request Received' : config.title}
        </Text>
        {!submitted && (
          <>
            <Text variant="body1" color="text.secondary" mb={0.5}>
              {config.subtitle}
            </Text>
            <Text variant="caption" color="text.secondary">
              All fields required
            </Text>
          </>
        )}
      </Box>

      <DialogContent sx={{ px: 4, pb: 4, pt: 1 }}>
        {submitted ? (
          <SuccessView onClose={handleClose} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2}>
              {isAccountError && (
                <Alert severity="warning">
                  Your account could not be loaded. Please refresh and try
                  again.
                </Alert>
              )}
              {(lead.isError || hubspot.isError) && (
                <Alert severity="error">
                  Something went wrong. Please try again or contact support.
                </Alert>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Input
                  placeholder="FIRST NAME"
                  required
                  fullWidth
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  label=""
                  sx={placeholderSx}
                />
                <Input
                  placeholder="LAST NAME"
                  required
                  fullWidth
                  {...register('lastName')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  label=""
                  sx={placeholderSx}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Input
                  placeholder={config.emailPlaceholder}
                  type="email"
                  required
                  fullWidth
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  label=""
                  sx={placeholderSx}
                />
                <Input
                  placeholder="PHONE"
                  type="tel"
                  required
                  fullWidth
                  {...register('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  label=""
                  sx={placeholderSx}
                />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Input
                  placeholder="COMPANY WEBSITE URL"
                  required
                  fullWidth
                  {...register('company')}
                  error={!!errors.company}
                  helperText={errors.company?.message}
                  label=""
                  sx={placeholderSx}
                />
                <SelectField
                  name="industry"
                  register={register}
                  options={INDUSTRY_OPTIONS}
                  placeholder="Industry"
                  error={!!errors.industry}
                  helperText={errors.industry?.message}
                />
              </Stack>

              {industry === 'Other' && (
                <Input
                  placeholder="PLEASE SPECIFY YOUR INDUSTRY"
                  required
                  fullWidth
                  {...register('industryOther')}
                  error={!!errors.industryOther}
                  helperText={errors.industryOther?.message}
                  label=""
                  sx={placeholderSx}
                />
              )}

              <UseCasesField
                control={control}
                register={register}
                errors={errors}
                showApiOption={variant === 'upgrade'}
                label={config.useCasesLabel}
              />

              <Box sx={sectionBoxSx}>
                <Text
                  variant="caption"
                  sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Describe your intended use case
                </Text>
                <Text
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 1.5 }}
                >
                  Describe specifically what you&apos;re trying to accomplish
                  and which data points you need. The more detail you provide,
                  the faster we can evaluate your request. Vague or incomplete
                  responses may slow down approval.
                </Text>
                <Input
                  placeholder={config.useCasePlaceholder}
                  required
                  fullWidth
                  multiline
                  rows={3}
                  {...register('useCaseDescription')}
                  error={!!errors.useCaseDescription}
                  helperText={errors.useCaseDescription?.message}
                  label=""
                  sx={textareaSx}
                />
              </Box>

              <Box sx={sectionBoxSx}>
                <Text
                  variant="caption"
                  sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  {config.estimatedLabel}
                </Text>
                <SelectField
                  name="estimatedRequests"
                  register={register}
                  options={ESTIMATED_REQUESTS_OPTIONS}
                  placeholder="Select"
                  error={!!errors.estimatedRequests}
                  helperText={errors.estimatedRequests?.message}
                />
              </Box>

              <Box sx={{ textAlign: 'center', pt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isAccountLoading || isAccountError}
                  startIcon={
                    isSubmitting || isAccountLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : null
                  }
                  sx={{ borderRadius: 6, px: 6, py: 1.5, fontSize: '1rem' }}
                >
                  {isSubmitting
                    ? 'Submitting…'
                    : isAccountLoading
                    ? 'Loading…'
                    : 'Submit'}
                </Button>
              </Box>
            </Stack>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
